import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Plus, Trash2, Save, Smartphone } from "lucide-react";
import swal from "sweetalert";
import axiosInstance from "../../../redux/api";
import {
  createDynamicSection,
  updateDynamicSection,
  deleteDynamicSection,
} from "../../../redux/home/action";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import HomePageTabs from "../Banners/HomePageTabs";
import "./HomePageCRM.css";

/**
 * The mobile home screen's trust strip — the one-line row of guarantees that
 * sits directly under the hero.
 *
 * WHY IT IS EDITABLE HERE. The claims used to be compiled into the app, so
 * rewording "free delivery over 300" meant a release and two store reviews.
 * Marketing owns this copy now; the app re-reads it on every pull-to-refresh.
 *
 * MOBILE ONLY, AND NOT A CHOICE. There is no surface picker on this screen.
 * The website has no component that renders the strip, so a claim published
 * to web would be copy that looks live here and is invisible to every
 * shopper. The API refuses any surface but 'app', and this screen never
 * offers one.
 *
 * Claims are stored as rows in the same table as homepage sections
 * (block_type 'strip'), which is why the create/update/delete actions below
 * are the section ones with a section_type of 'strip'.
 */

// The app lays the strip out in one line and scrolls it rather than
// truncating, so this is guidance and not a limit — Arabic runs materially
// longer than English for the same promise.
const COMFORTABLE_LENGTH = 22;

const BLANK = { name: "", ar_name: "", is_active: true };

const TrustStrip = () => {
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [claims, setClaims] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [newClaim, setNewClaim] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [previewArabic, setPreviewArabic] = useState(false);

  const editable = hasAction(MENU.HOME, PERMISSION.EDIT);
  const deletable = hasAction(MENU.HOME, PERMISSION.DELETE);

  // Fetched straight from the API rather than through the shared sections
  // slice: both screens would otherwise write to the same key and each would
  // briefly render the other's rows when switching tabs.
  const fetchClaims = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("admin/sections/", {
        params: { section_type: "strip", per_page: 50 },
      });
      const rows = res?.data?.status ? res?.data?.response?.data : [];
      setClaims(
        [...(Array.isArray(rows) ? rows : [])].sort(
          (a, b) => (a.position ?? 0) - (b.position ?? 0)
        )
      );
      setDrafts({});
    } catch (err) {
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const draftFor = (claim) => ({ ...claim, ...(drafts[claim.id] || {}) });

  const isDirty = (claim) => {
    const draft = drafts[claim.id];
    if (!draft) return false;
    return Object.keys(draft).some((key) => draft[key] !== claim[key]);
  };

  const editField = (id, field, value) =>
    setDrafts((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const handleSave = (claim) => {
    const draft = draftFor(claim);
    if (!draft.name?.trim() || !draft.ar_name?.trim()) {
      swal({
        text: "A claim needs both the English and the Arabic wording.",
        icon: "warning",
        button: "Close",
      });
      return;
    }

    dispatch(
      updateDynamicSection(
        {
          id: claim.id,
          section_type: "strip",
          name: draft.name.trim(),
          ar_name: draft.ar_name.trim(),
          position: Number(draft.position) || 1,
          is_active: !!draft.is_active,
        },
        (val) => setSavingId(val ? claim.id : null),
        fetchClaims
      )
    );
  };

  const handleCreate = () => {
    if (!newClaim.name.trim() || !newClaim.ar_name.trim()) {
      swal({
        text: "A claim needs both the English and the Arabic wording.",
        icon: "warning",
        button: "Close",
      });
      return;
    }

    dispatch(
      createDynamicSection(
        {
          section_type: "strip",
          name: newClaim.name.trim(),
          ar_name: newClaim.ar_name.trim(),
          is_active: newClaim.is_active,
        },
        setCreating,
        () => {
          setNewClaim(BLANK);
          fetchClaims();
        }
      )
    );
  };

  const handleDelete = (claim) => {
    swal({
      title: "Delete claim",
      text: `Remove "${claim.name}" from the strip?`,
      icon: "warning",
      buttons: ["Cancel", "Delete"],
      dangerMode: true,
    }).then((confirm) => {
      if (confirm) {
        dispatch(
          deleteDynamicSection(
            claim.id,
            (val) => setSavingId(val ? claim.id : null),
            fetchClaims
          )
        );
      }
    });
  };

  const liveClaims = claims
    .map(draftFor)
    .filter((claim) => claim.is_active)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  return (
    <div className="ds-container">
      <div className="ds-header">
        <div>
          <h4>Trust Strip</h4>
          <p>
            The line of guarantees under the hero on the mobile app. Changes go
            live on the shopper&apos;s next refresh — no app release needed.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <HomePageTabs />
      </div>

      <div className="ts-notice">
        <Smartphone size={15} />
        <span>
          <strong>Mobile app only.</strong> The website does not show this
          strip, so there is no surface to choose.
        </span>
      </div>

      {/* What the shopper actually sees. Worth the space: these claims are
          read in one glance, and a line that wraps or runs long reads very
          differently here than it does in a table cell. */}
      <div className="ts-preview">
        <div className="ts-preview__head">
          <span>Preview</span>
          <div className="ts-preview__langs">
            <button
              type="button"
              className={!previewArabic ? "is-active" : ""}
              onClick={() => setPreviewArabic(false)}
            >
              English
            </button>
            <button
              type="button"
              className={previewArabic ? "is-active" : ""}
              onClick={() => setPreviewArabic(true)}
            >
              العربية
            </button>
          </div>
        </div>
        <div
          className="ts-preview__strip"
          dir={previewArabic ? "rtl" : "ltr"}
        >
          {liveClaims.length === 0 ? (
            <span className="ts-preview__empty">
              {claims.length === 0
                ? "No claims yet — the app falls back to its own wording."
                : "Every claim is off — the app shows no strip at all."}
            </span>
          ) : (
            liveClaims.map((claim) => (
              <span key={claim.id} className="ts-preview__claim">
                {(previewArabic ? claim.ar_name : claim.name) || "—"}
              </span>
            ))
          )}
        </div>
      </div>

      <div className="ds-table-wrapper">
        <table className="ds-table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>Order</th>
              <th>English</th>
              <th>Arabic</th>
              <th style={{ width: 90 }}>Status</th>
              {editable && <th style={{ width: 110 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={editable ? 5 : 4} className="ts-empty">
                  Loading…
                </td>
              </tr>
            ) : claims.length === 0 ? (
              <tr>
                <td colSpan={editable ? 5 : 4} className="ts-empty">
                  No claims yet. While this list is empty the app shows the
                  wording built into it.
                </td>
              </tr>
            ) : (
              claims.map((claim) => {
                const draft = draftFor(claim);
                const dirty = isDirty(claim);
                return (
                  <tr key={claim.id}>
                    <td>
                      <input
                        type="number"
                        className="ts-input ts-input--pos"
                        value={draft.position ?? ""}
                        disabled={!editable}
                        onChange={(e) =>
                          editField(claim.id, "position", e.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="ts-input"
                        value={draft.name || ""}
                        disabled={!editable}
                        onChange={(e) => editField(claim.id, "name", e.target.value)}
                      />
                      <LengthHint value={draft.name} />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="ts-input"
                        dir="rtl"
                        value={draft.ar_name || ""}
                        disabled={!editable}
                        onChange={(e) =>
                          editField(claim.id, "ar_name", e.target.value)
                        }
                      />
                      <LengthHint value={draft.ar_name} />
                    </td>
                    <td>
                      <label className="ts-toggle">
                        <input
                          type="checkbox"
                          checked={!!draft.is_active}
                          disabled={!editable}
                          onChange={(e) =>
                            editField(claim.id, "is_active", e.target.checked)
                          }
                        />
                        <span>{draft.is_active ? "Live" : "Off"}</span>
                      </label>
                    </td>
                    {editable && (
                      <td>
                        <div className="ds-actions">
                          <button
                            className="ds-btn-edit"
                            title="Save"
                            disabled={!dirty || savingId === claim.id}
                            onClick={() => handleSave(claim)}
                          >
                            <Save size={12} />
                          </button>
                          {deletable && (
                            <button
                              className="ds-btn-delete"
                              title="Delete"
                              disabled={savingId === claim.id}
                              onClick={() => handleDelete(claim)}
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}

            {editable && (
              <tr className="ts-new-row">
                <td>
                  <span className="ts-new-badge">New</span>
                </td>
                <td>
                  <input
                    type="text"
                    className="ts-input"
                    placeholder="100% Authentic"
                    value={newClaim.name}
                    onChange={(e) =>
                      setNewClaim({ ...newClaim, name: e.target.value })
                    }
                  />
                  <LengthHint value={newClaim.name} />
                </td>
                <td>
                  <input
                    type="text"
                    className="ts-input"
                    dir="rtl"
                    placeholder="أصلي 100%"
                    value={newClaim.ar_name}
                    onChange={(e) =>
                      setNewClaim({ ...newClaim, ar_name: e.target.value })
                    }
                  />
                  <LengthHint value={newClaim.ar_name} />
                </td>
                <td>
                  <label className="ts-toggle">
                    <input
                      type="checkbox"
                      checked={newClaim.is_active}
                      onChange={(e) =>
                        setNewClaim({ ...newClaim, is_active: e.target.checked })
                      }
                    />
                    <span>{newClaim.is_active ? "Live" : "Off"}</span>
                  </label>
                </td>
                <td>
                  <button
                    className="ds-btn-primary ts-add-btn"
                    disabled={creating}
                    onClick={handleCreate}
                  >
                    <Plus size={14} /> Add
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="ts-footnote">
        Claims are written out in full, with no automatic numbers — that is what
        lets you reword one here instead of shipping a build. It also means a
        figure typed here is not checked against what checkout charges, so if
        you change the free-delivery threshold, change it in Settings too.
      </p>
    </div>
  );
};

/** Soft nudge, never a block — long copy scrolls rather than truncating. */
const LengthHint = ({ value }) => {
  const length = (value || "").trim().length;
  if (length <= COMFORTABLE_LENGTH) return null;
  return (
    <span className="ts-hint">{length} characters — long claims scroll</span>
  );
};

export default TrustStrip;
